import {
  Combinator,
  MultiValueSearchField,
  SearchCombo,
  SingleValueSearchField,
} from 'modules/workflows';
import { TriggerHoldUntilReplyRuleType } from 'modules/workflows/constants/nodes/holdUntilReply';
import { v4 as uuidv4 } from 'uuid';
import {
  CAMPAIGN_ID,
  HAS_OTHER_REPLY,
  REPLIES,
  TRIGGER_RULE_TYPE,
} from './HoldUntilReply.constants';
import { Reply, Variant } from './HoldUntilReply.types';

enum DataType {
  SMS = 'smsReceived',
  WhatsApp = 'whatsAppReceived',
}

enum FieldValue {
  SMS = 'smsMessage',
  WhatsApp = 'sanitized.inputText',
}

enum ComparatorType {
  EQUALS = 'Equals',
  IS_SET = 'IsSet',
}

const getDataTypeValue = (type: TriggerHoldUntilReplyRuleType) =>
  type === TriggerHoldUntilReplyRuleType.SMS ? DataType.SMS : DataType.WhatsApp;

const getFieldValue = (type: TriggerHoldUntilReplyRuleType) =>
  type === TriggerHoldUntilReplyRuleType.SMS ? FieldValue.SMS : FieldValue.WhatsApp;
/*
{
    "dataType": "whatsAppReceived",
    "field": "sanitized.inputText",
    "comparatorType": "Equals",
    "values": [
      "hi",
      "hello",
      "hey"
    ]

}
*/

const createMultiValueSearchFieldForReplyVariations = (
  type: TriggerHoldUntilReplyRuleType,
  variations: string[],
): MultiValueSearchField => {
  return {
    dataType: getDataTypeValue(type),
    field: getFieldValue(type),
    comparatorType: ComparatorType.EQUALS,
    values: variations,
  };
};

/*
{
  "dataType": "whatsAppReceived",
  "field": "sanitized.inputText",
  "comparatorType": "IsSet",
  "value": "",
  "fieldType": "string"
}
*/

const createIsSetField = (type: TriggerHoldUntilReplyRuleType): SingleValueSearchField => {
  return {
    dataType: getDataTypeValue(type),
    field: getFieldValue(type),
    comparatorType: ComparatorType.IS_SET,
    value: '',
    fieldType: 'string',
  };
};

const createOrSearchCombo = (
  type: TriggerHoldUntilReplyRuleType,
  repliesSearchFields: MultiValueSearchField[],
  hasOtherReply: boolean,
): SearchCombo => {
  return {
    combinator: Combinator.Or,
    searchQueries: hasOtherReply
      ? [...repliesSearchFields, createIsSetField(type)]
      : repliesSearchFields,
  };
};

/*
{
  "dataType": "whatsAppReceived",
  "field": "campaignId",
  "comparatorType": "Equals",
  "value": "12345",
  "fieldType": "long"
}
*/
const createCampaignField = (
  type: TriggerHoldUntilReplyRuleType,
  campaignId: string,
): SingleValueSearchField => {
  return {
    dataType: getDataTypeValue(type),
    field: CAMPAIGN_ID,
    comparatorType: ComparatorType.EQUALS,
    value: campaignId,
    fieldType: 'long',
  };
};

type TriggerRuleInputs = {
  type: TriggerHoldUntilReplyRuleType;
  replies: string[][];
  hasOtherReply: boolean;
  campaignId?: string;
  triggerRuleId?: string;
};

export type TriggerRule = {
  triggerRuleType: TriggerHoldUntilReplyRuleType;
  id?: string;
  searchQuery: SearchCombo;
};

export const createTriggerRule = ({
  type,
  replies,
  hasOtherReply,
  campaignId,
  triggerRuleId,
}: TriggerRuleInputs): TriggerRule => {
  const filteredReplies = replies.filter((r) => r.length > 0); // make sure we actually have replies and not empty arrays
  const repliesSearchFields = filteredReplies.map((r) =>
    createMultiValueSearchFieldForReplyVariations(type, r),
  );
  const orCombo = createOrSearchCombo(type, repliesSearchFields, hasOtherReply);
  const searchQueries = campaignId ? [createCampaignField(type, campaignId), orCombo] : [orCombo];
  const searchQuery: SearchCombo = {
    combinator: Combinator.And,
    searchQueries,
  };

  return {
    id: triggerRuleId ?? uuidv4(), // use existing id if available
    triggerRuleType: type,
    searchQuery,
  };
};

type HoldUntilReplyTriggerRuleConfig = {
  type: TriggerHoldUntilReplyRuleType;
  replies: string[][];
  hasOtherReply: boolean;
  campaignId?: string;
};

/* parsing the Trigger rule into UI friendly objects */
export const parseTriggerRule = (rule: TriggerRule): HoldUntilReplyTriggerRuleConfig => {
  // parse the campaign specific query
  const campaignIdQuery = rule.searchQuery.searchQueries.filter((q) => 'comparatorType' in q);
  const campaignId =
    campaignIdQuery.length > 0 ? (campaignIdQuery[0] as SingleValueSearchField).value : undefined;

  // parse the reply specific query
  const repliesQuery = rule.searchQuery.searchQueries.filter((q) => 'combinator' in q);
  const repliesQueries =
    repliesQuery.length > 0 ? (repliesQuery[0] as SearchCombo).searchQueries : [];
  const replies = repliesQueries.map((q) => (q as MultiValueSearchField).values);

  const result = {
    type: rule.triggerRuleType,
    hasOtherReply: replies.length > 0 ? !Array.isArray(replies[replies.length - 1]) : false,
    replies: replies.filter((r) => Array.isArray(r)),
    campaignId,
  };

  return result;
};

/**
 * Parse replies from trigger rule to expected RHF format
 */
export const parseRepliesForForm = (replies: string[][]): Reply[] => {
  const formattedReplies = replies.map((replySet: string[]) => ({
    variants: replySet.map((reply: string) => ({
      variant: reply,
      readOnly: false, // TODO: update this when we have a way to determine if the reply is from the campaign
    })),
  }));

  return formattedReplies;
};

/**
 * Parse RHF replies values to expected trigger rule format
 */
const parseRepliesForTriggerRule = (replies: Reply[]): string[][] =>
  replies.map((replySet: Reply) =>
    replySet.variants.flatMap(({ variant }: Variant) => {
      return variant;
    }),
  );

/**
 * Parse all RHF values to expected trigger rule format
 */
export const parseValuesForTriggerRule = (
  values: Record<string, any>,
): {
  type: TriggerHoldUntilReplyRuleType;
  replies: string[][];
  hasOtherReply: boolean;
  campaignId?: string;
} => {
  const type = values[TRIGGER_RULE_TYPE];
  const campaignId =
    type === TriggerHoldUntilReplyRuleType.WHATSAPP
      ? values[CAMPAIGN_ID]
        ? `${values[CAMPAIGN_ID]}`
        : undefined
      : undefined;
  const replies = parseRepliesForTriggerRule(values[REPLIES]);
  const hasOtherReply = values[HAS_OTHER_REPLY];

  return {
    type,
    campaignId,
    replies,
    hasOtherReply,
  };
};
